import styled from '@emotion/styled'

import {
  AssetInformationModal,
  Icon,
  LinkIcon,
  MasterDetail,
  MessageBox,
  PerformanceChart,
  ProjectionChart,
  Section,
  Space
} from '@/components/core-v2'
import { ChooseAssetModal } from '@/modules/tradebuilder-v2/modules/03-portfolio/choose-assets'

import { EditPortfoliosActions, EditPortfoliosState } from '../../state/edit-portfolio-slice'
import { PortfolioDetailsPanel } from '../portfolio-details-panel/portfolio-details-panel'
import { PortfoliosPanel } from '../portfolios-panel/portfolios-panel'

export type PortfoliosLayoutProps = {
  actions: EditPortfoliosActions
  areDisallowedStrategiesOverridden: boolean
  canUserSave: boolean
  canUserStartNew: boolean
  state: EditPortfoliosState
}

const StyledContentSection = styled.section`
  height: calc(100vh - 109px);
`

const StyledCharts = styled.section`
  height: calc(100vh - 109px);
  overflow-y: auto;
`

const PortfoliosLayout = ({
  actions,
  areDisallowedStrategiesOverridden,
  canUserSave,
  canUserStartNew,
  state
}: PortfoliosLayoutProps) => (
  <StyledContentSection>
    <MasterDetail
      detailChildren={
        <StyledCharts>
          {state.performanceMetrics && state.canDisplayCharts && (
            <Space my={3}>
              <PerformanceChart
                isError={state.isErrorGeneratingBacktest}
                isLoading={false}
                metrics={state.performanceMetrics}
                title="Historical performance"
              />
            </Space>
          )}
          {state.projection && state.canDisplayCharts && (
            <Space my={3}>
              <ProjectionChart
                isError={state.isErrorGeneratingProjection}
                isLoading={false}
                metrics={state.projection}
                title="Projections"
              />
            </Space>
          )}
          {!state.canDisplayCharts && state.isInspecting && (
            <Space px={5} py={6}>
              <MessageBox state="warning">Complete the form to display the charts</MessageBox>
            </Space>
          )}
        </StyledCharts>
      }
      masterChildren={
        <>
          {!state.isInspecting && (
            <Section type="primary">
              <PortfoliosPanel
                canUserStartNew={canUserStartNew}
                portfolios={state.portfolios}
                searchFilter={state.filter}
                selectedPortfolio={state.selectedPortfolio}
                onSearch={actions.search}
                onSelectNewPortfolio={actions.setIsNew}
                onSelectPortfolio={portfolioId => actions.fetchSelectedPortfolio(portfolioId)}
              />
            </Section>
          )}
          {state.isInspecting && (
            <Section pt={6} type="primary">
              <Space mb={5}>
                <LinkIcon
                  disabled={false}
                  icon={<Icon name="chevronLeft" size="small" />}
                  iconPosition="left"
                  text="Back to all portfolios"
                  onClick={actions.stopInspecting}
                />
              </Space>
              <PortfolioDetailsPanel
                actions={actions}
                allClients={state.allClients}
                approvalByPM={state.approvalByPM}
                approvalSuccess={state.approvalSuccess}
                areDisallowedStrategiesOverridden={areDisallowedStrategiesOverridden}
                assetPerformances={state.selectedAssetPerformance}
                availableCptyIds={state.availableCptyIds}
                availableStrategies={state.availableStrategies}
                canProceed={state.canProceed}
                canProceedApproval={state.canProceedApproval}
                canUserSave={canUserSave}
                errors={state.errors}
                isNew={state.isNew}
                portfolioUsage={state.selectedPortfolioUsage}
                resetApprovalStatus={actions.resetApprovalStatus}
                saveSuccess={state.saveSuccess}
                selectedAssetDuration={state.selectedAssetDuration}
                selectedPortfolio={state.selectedPortfolio}
                serverErrors={state.serverErrors}
                totalWeight={state.totalWeights}
                onApprovePortfolio={actions.approvePortfolio}
                onChangeApprovalStatus={(x: '' | 'Approved' | 'Rejected') =>
                  actions.setApprovalStatus(x)
                }
                onChangeApprovalUserName={x => actions.setApprovalUserName(x)}
              />
            </Section>
          )}
          {state.isChoosingAsset && (
            <ChooseAssetModal
              state={state.searchAsset}
              onChangeSearchTerm={term => actions.changeAssetSearchTerm(term)}
              onClickBackToSearch={() => actions.goBackToAssetSearch()}
              onInspectAsset={id => actions.inspectAssetForSelection(id)}
              onSelectAsset={asset => actions.addAsset(asset)}
              onStopChoosingAsset={() => actions.stopChoosingAsset()}
            />
          )}
          <AssetInformationModal
            assetId={state.assetBeingInspected?.assetId}
            isOpen={state.isInspectingAsset}
            onClose={() => actions.stopInspectingAsset()}
          />
        </>
      }
      width="content"
    />
  </StyledContentSection>
)

export default PortfoliosLayout
